import { getVaultMetadataByAddress } from '@/constants/nucleus-vaults';
import { GC_TIME_VERY_LONG, QueryKey, STALE_TIME_VERY_LONG, wrappedPlumeTokenAddress } from '@/constants/shared';
import { getTokenSymbolsFromAddressList } from '@/network/api';
import { nucleusClient } from '@/network/clients/nucleus';
import { NetApyResponse } from '@/pages/OptimizerVault/types';
import { useQuery } from '@tanstack/react-query';
import { useConfig } from 'wagmi';

export const useVaultApy = ({ vaultAddress }: { vaultAddress: string }) => {
  const wagmiConfig = useConfig();
  const { data: apyData, isLoading: isLoadingApyData } = useQuery({
    queryKey: [QueryKey.ApyDataWithBoostIncentives, vaultAddress],
    queryFn: async () => {
      if (!vaultAddress) throw new Error('No vault address supplied for hook useVaultApy');
      const vaultMetadata = getVaultMetadataByAddress(vaultAddress);
      const apyResponse: NetApyResponse = await nucleusClient.get('plume/net-apy', {
        vaultAddress,
      });
      const { boostIncentives, underlyingRate, netApy } = apyResponse;
      const tokenSymbolsAndAddresses = await getTokenSymbolsFromAddressList(Object.keys(boostIncentives), wagmiConfig);
      const boostIncentivesList = tokenSymbolsAndAddresses.map(({ tokenAddress, tokenSymbol }) => {
        return {
          tokenAddress,
          tokenSymbol,
          apy: boostIncentives[tokenAddress],
        };
      });
      const totalBoostApy = Object.values(boostIncentives).reduce((total, next) => total + next, 0);
      return {
        ...apyResponse,
        netApy: vaultMetadata.apyOverride ? vaultMetadata.apyOverride + totalBoostApy : netApy,
        underlyingRate: vaultMetadata.apyOverride ?? underlyingRate,
        wPlumeApy: boostIncentives[wrappedPlumeTokenAddress],
        boostIncentivesList,
      };
    },
    staleTime: STALE_TIME_VERY_LONG,
    gcTime: GC_TIME_VERY_LONG,
  });
  const { boostIncentivesList } = apyData ?? {};
  return {
    apyData,
    boostIncentivesList,
    isLoadingApyData,
  };
};
